import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

// @mui material components
// @mui material components
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
/* import DataTable from "examples/Tables/DataTable"; */

import { getUsers, getSpecificUsers, deleteUser } from "../../../../api/users";

import DeleteModal from "../../../../customComponents/devicesComponents/DeleteModal";

import CreateEditUser from "../../../../customComponents/adminComponents/CreateEditUser";
import ChangePasswordModal from "../../../../customComponents/adminComponents/ChangePasswordModal";

import { areaColor, userRoleEnum, areaEnum } from "../../../../config/enum";

import { setAllUsers } from "../../../../redux/reducers/usersReducer";

import {
  setUserArea,
  setUserRole,
  setUserSuperior,
  setUserValueBigFilter,
  setUserSelectBigFilter,
} from "../../../../redux/reducers/filtersReducer";

import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";

export default function Users() {
  useAuthCheck();
  const { user } = useAuth();
  const [users, setUsers] = useState(null);

  const userRole = useSelector((state) => state.filters.userRole);
  const userArea = useSelector((state) => state.filters.userArea);
  const userSuperior = useSelector((state) => state.filters.userSuperior);
  const userValueBigFilter = useSelector((state) => state.filters.userValueBigFilter);
  const userSelectBigFilter = useSelector((state) => state.filters.userSelectBigFilter);

  const [superiors, setSuperiors] = useState([]);
  const [page, setPage] = useState(1);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const [controller] = useMaterialUIController();
  const navigate = useNavigate();
  const { darkMode, cardBoard } = controller;

  const dispatch = useDispatch();

  const bigFilterInfo = {
    Nom: "tbluser.last_name",
    Prénom: "tbluser.first_name",
  };

  const [query, setQuery] = useState({});

  // Combined useEffect for updating query based on filters and fetching orders
  const updateQueryBasedOnFilters = () => {
    let updatedQuery = {};
    if (userArea) updatedQuery["tbluser.user_areas"] = userArea;
    if (userRole) updatedQuery["tbluser.role"] = userRole;
    if (userSuperior) updatedQuery["tbluser.superior"] = userSuperior?.id || null;
    if (userSelectBigFilter) {
      updatedQuery[bigFilterInfo[userSelectBigFilter]] = encodeURIComponent(userValueBigFilter);
    }
    return updatedQuery;
  };

  const fetchUsersWithFilters = async (additionalQuery = {}) => {
    if (user) {
      const filtersQuery = updateQueryBasedOnFilters();
      const response = await getUsers({ ...filtersQuery, ...query, ...additionalQuery });
      setUsers(response.data);
    }
  };

  const fetchUsers = async () => {
    const response = await getUsers();
    const formatedUsers = await response.data.map((user) => ({
      label: user.name,
      id: user.user_id,
    }));
    dispatch(setAllUsers(formatedUsers));
  };

  function updateQuery(key, value) {
    if (value === null)
      setQuery((query) => {
        const copy = { ...query };
        delete copy[key];
        return copy;
      });
    else setQuery((query) => ({ ...query, [key]: value }));
  }

  async function deleteUserAPI(user) {
    await deleteUser(user);
  }

  function paginate(array, page_size, page_number) {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }

  async function submit(user) {
    await deleteUserAPI(user);
    await fetchUsersWithFilters();
    await fetchUsers();
  }

  async function fetchSuperiors() {
    const response = await getSpecificUsers("superiors");
    setSuperiors(response.data);
  }

  useEffect(() => {
    fetchSuperiors();
  }, []);

  useEffect(() => {
    if (user && user.role !== "Admin" && user.role !== "Pilote") {
      navigate("/accueil");
    } else {
      fetchUsersWithFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userArea, userRole, userSuperior, userValueBigFilter, userSelectBigFilter, dispatch, user]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={1.5}>
        <Grid container justifyContent="center" direction="row" spacing={2}>
          <Grid
            item
            lg={2.8}
            md={4}
            sm={5}
            xs={10}
            sx={{
              border: "1px solid lightGrey",
              borderRadius: "8px",
              marginTop: 2,
              marginLeft: 2,
              padding: "1rem !important",
            }}
          >
            <MDTypography variant="subtitle1" align="center">
              Filtre de précision
            </MDTypography>
            <Tooltip
              title={userSelectBigFilter ? "" : "Veuillez sélectionner un élément à cibler"}
              placement="top"
            >
              <div>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={userValueBigFilter}
                  onChange={(event) => {
                    dispatch(setUserValueBigFilter(event.target.value));
                    updateQuery(bigFilterInfo[userSelectBigFilter], event.target.value);
                    setPage(1);
                  }}
                  inputProps={{
                    style: {
                      height: 29.13,
                    },
                    maxLength: 255,
                  }}
                  type="text"
                  label="Valeur à rechercher"
                  disabled={!userSelectBigFilter}
                  sx={{ marginBottom: 2, marginTop: 1 }}
                />
              </div>
            </Tooltip>
            <Autocomplete
              options={Object.keys(bigFilterInfo)}
              value={userSelectBigFilter}
              onChange={(event, newBigFilter) => {
                if (newBigFilter) {
                  // If a new filter is selected, update the query and keep the current input value
                  dispatch(setUserSelectBigFilter(newBigFilter));
                } else {
                  // If the filter is cleared, remove the corresponding query part and clear the input
                  setQuery((currentQuery) => {
                    const newQuery = { ...currentQuery };
                    Object.values(bigFilterInfo).forEach((filterKey) => {
                      if (filterKey in newQuery) {
                        delete newQuery[filterKey];
                      }
                    });
                    return newQuery;
                  });
                  dispatch(setUserSelectBigFilter(null));
                  dispatch(setUserValueBigFilter(""));
                }
                setPage(1);
              }}
              renderInput={(params) => (
                <MDInput pb={0} {...params} label="Élément à cibler" variant="outlined" />
              )}
            />
          </Grid>

          <Grid
            item
            container
            direction="column"
            justifyContent="center"
            spacing={2}
            lg={4.4}
            md={4}
            sm={5}
            xs={11}
            sx={{
              padding: "1rem !important",
              border: "1px solid lightGrey",
              borderRadius: "8px",
              textAlign: "center",
              marginTop: 2,
              marginLeft: 2,
            }}
          >
            <MDTypography mb={1} variant="subtitle1" align="center">
              Filtres normaux
            </MDTypography>
            <Grid direction="row" spacing={2} container justifyContent="center">
              <Grid item lg={6} md={12} sm={12} xs={12}>
                <Autocomplete
                  options={userRoleEnum}
                  value={userRole}
                  onChange={(event, newUserRole) => {
                    dispatch(setUserRole(newUserRole));
                    updateQuery("tbluser.role", newUserRole);
                    setPage(1);
                  }}
                  renderInput={(params) => (
                    <MDInput pb={0} {...params} label="Rôle" variant="outlined" />
                  )}
                />
              </Grid>
              <Grid item lg={6} md={12} sm={12} xs={12}>
                <Autocomplete
                  options={areaEnum}
                  value={userArea}
                  onChange={(event, newUserArea) => {
                    dispatch(setUserArea(newUserArea));
                    updateQuery("tbluser.user_areas", newUserArea);
                    setPage(1);
                  }}
                  renderInput={(params) => (
                    <MDInput pb={0} {...params} label="Région" variant="outlined" />
                  )}
                  renderOption={(props, option) => (
                    <MDBox
                      component="li"
                      {...props}
                      style={{ color: areaColor[option], fontWeight: "bold" }}
                    >
                      {option}
                    </MDBox>
                  )}
                />
              </Grid>
              <Grid item lg={6} md={12} sm={12} xs={12}>
                <Autocomplete
                  options={superiors}
                  value={userSuperior}
                  onChange={(event, newSuperior) => {
                    dispatch(setUserSuperior(newSuperior));
                    updateQuery("tbluser.superior", newSuperior?.id || null);
                    setPage(1);
                  }}
                  renderInput={(params) => (
                    <MDInput pb={0} {...params} label="Supérieur" variant="outlined" />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            direction="row"
            item
            container
            justifyContent="center"
            lg={12}
            md={12}
            sm={12}
            xs={12}
          >
            <Grid item sx={{ padding: 1, marginTop: 3 }} lg={3} md={6} sm={6} xs={12}>
              <CreateEditUser
                users={users}
                fetchUsersWithFilters={fetchUsersWithFilters}
                edit={false}
              />
            </Grid>
          </Grid>
        </Grid>
      </MDBox>

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {users ? (
            !cardBoard ? (
              paginate(users, 12, page).map((user) => (
                <Grid key={user.user_id} item mb={1.5} xl={3} lg={4} md={6} xs={12}>
                  <Card sx={{ padding: "0 1rem" }}>
                    <MDBox
                      mx={0.5}
                      mt={-3}
                      py={3}
                      px={2}
                      sx={{
                        background:
                          user.user_areas && user.user_areas.length > 0
                            ? areaColor[user.user_areas[0]]
                            : !darkMode
                            ? "linear-gradient(195deg, #364c70, #273956)"
                            : "white",
                        boxShadow: `0 1px 6px 1px ${
                          user.user_areas && user.user_areas.length > 0
                            ? areaColor[user.user_areas[0]]
                            : !darkMode
                            ? "#344767"
                            : "white"
                        }`,
                      }}
                      variant="gradient"
                      borderRadius="lg"
                    >
                      <MDTypography
                        variant="h5"
                        textAlign="center"
                        sx={{
                          color:
                            user.user_areas && user.user_areas.length > 0
                              ? "white !important"
                              : !darkMode
                              ? "white !important"
                              : "#344767",
                        }}
                      >
                        {user.name ? user.name : "Nom inconnu"}
                      </MDTypography>
                    </MDBox>
                    <MDBox textAlign="center" pb={3} pt={3}>
                      {/* <MDTypography
                      opacity="0.7"
                      fontSize="0.75rem"
                      textTransform="uppercase"
                      textAlign="center"
                    >
                      Dernière modification
                    </MDTypography>
                    <MDTypography mt={-0.7} variant="h6" textAlign="center">
                      {order.date ? `${order.date}` : "Date et heure inconnue"}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      mt={-0.1}
                      textAlign="center"
                    >
                      {order.username ? `Par ${order.username}` : "Par un utilisateur inconnue"}
                    </MDTypography> */}
                      <MDTypography
                        /* mt={1.5} */
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Rôle
                      </MDTypography>
                      <MDTypography mt={-0.7} variant="h6" textAlign="center">
                        {user.role !== null ? user.role : "Rôle inconnu"}
                      </MDTypography>
                      <MDTypography
                        mt={1.5}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Région
                      </MDTypography>
                      <MDTypography mt={-0.7} variant="h6" textAlign="center">
                        {user.user_areas && user.user_areas.length > 0 ? user.user_areas.join(", ") : "Région inconnu"}
                      </MDTypography>
                      <Grid
                        container
                        mt={0.25}
                        rowSpacing={1.5}
                        columnSpacing={{ xs: 2 }}
                        justifyContent="center"
                      >
                        <Grid item>
                          <CreateEditUser
                            users={users}
                            fetchUsersWithFilters={fetchUsersWithFilters}
                            user={user}
                            edit
                          />
                        </Grid>
                        <Grid item>
                          <ChangePasswordModal user={user} />
                        </Grid>
                        <Grid item>
                          <DeleteModal
                            message={`l'utilisateur ${user.username}`}
                            onOk={() => {
                              submit(user.user_id);
                            }}
                            type="user"
                          />
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              ))
            ) : (
              paginate(users, 12, page).map((user) => (
                <Grid
                  key={user.user_id}
                  item
                  container
                  xs={12}
                  sx={{ paddingTop: "0px !important", marginBottom: "0px !important" }}
                >
                  <Grid item mt={0.5} mb={0.5} xs={12}>
                    <Divider
                      sx={{
                        margin: "0.5rem 0rem",
                        background: !darkMode ? "#000" : "#FFF",
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
                  >
                    <Grid item lg={2.5} xs={12}>
                      <MDTypography
                        mt={0.75}
                        variant="h5"
                        textAlign="center"
                        sx={{
                          color:
                            user.user_areas && user.user_areas.length > 0
                              ? areaColor[user.user_areas[0]]
                              : !darkMode
                              ? "inherit"
                              : "light",
                        }}
                      >
                        {user.name ? user.name : "Nom inconnu"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={1.5} xs={12}>
                      <MDTypography
                        mt={0.75}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Rôle
                      </MDTypography>
                      <MDTypography mt={-0.7} variant="h6" textAlign="center">
                        {user.role !== null ? user.role : "Rôle inconnu"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2} xs={12}>
                      <MDTypography
                        mt={0.75}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Région
                      </MDTypography>
                      <MDTypography mt={-0.7} variant="h6" textAlign="center">
                        {user.user_areas && user.user_areas.length > 0 ? user.user_areas.join(", ") : "Région inconnu"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={1.5} xs={12} textAlign="center">
                      <CreateEditUser
                        users={users}
                        fetchUsersWithFilters={fetchUsersWithFilters}
                        user={user}
                        edit
                      />
                    </Grid>
                    <Grid item lg={1.5} xs={12} textAlign="center">
                      <ChangePasswordModal user={user} />
                    </Grid>
                    <Grid item lg={1.5} xs={12} textAlign="center">
                      <DeleteModal
                        message={`l'utilisateur ${user.username}`}
                        onOk={() => {
                          submit(user.user_id);
                        }}
                        type="user"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))
            )
          ) : (
            <Grid item xs={12} justifyContent="center">
              <MDTypography pb={2} variant="h5" textAlign="center">
                Aucun utilisateur à afficher
              </MDTypography>
            </Grid>
          )}
          {users &&
            (users.length <= 12 ? (
              <></>
            ) : (
              <Grid item container xs={12} justifyContent="center">
                <Stack spacing={2}>
                  <Pagination
                    page={page}
                    onChange={handlePageChange}
                    variant="text"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      button: {
                        color: !darkMode ? "#1A2035" : "#FFF",
                      },
                      "button.Mui-selected": { color: !darkMode ? "#FFF" : "#1A2035" },
                    }}
                    count={Math.ceil(users.length / 12)}
                    color={darkMode ? "white" : "dark"}
                  ></Pagination>
                </Stack>
              </Grid>
            ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
